import { Component, Mixins, Watch } from 'vue-property-decorator';
import isEqual from 'lodash.isequal';
import { digits } from '@nodes/helpers/number';
import { date, currentDate } from '@nodes/helpers/date';
import { formElements } from '@nodes/helpers/forms';
import CoreMixin from '@nodes/mixins/Core';
import HybridMixin from '@rogues/shared/mixins/hybrid';
import { YPanel, YForm } from '@components/index';
import YAvatar from '@rogues/ProfileUserInfo/blocks/Avatar/Avatar.vue';
import Props from './props';

@Component({
    components: {
        YPanel,
        YForm,
        YAvatar,
    },
})

/**
 * @name ProfileUserInfo
 */
export default class ProfileUserInfo extends Mixins(HybridMixin, CoreMixin, Props) {
    userInfo: Array<{ [k: string]: any }> = [];
    model: any = {};
    digits = digits;
    date = date;
    currentDate = currentDate;

    /**
     * Get User Avatar
     *
     * @returns {object}
     */
    get avatar(): object {
        let img = null;
        if (this.user && this.user.avatar && this.user.avatar.length) {
            [img] = this.userMetadata.avatar.resource;
        }
        return img;
    }

    /**
     * Return Form Elements
     *
     * @returns {*}
     */
    get formElementsArray(): any {
        return this.userMetadata ? formElements(this.userMetadata.fields[0].fields, ['username', 'address']) : [];
    }

    /**
     * Call Method for creating user info
     */
    @Watch('isEdit', { deep: true })
    onEditMode(): any {
        this.createUserModel();
    }

    /**
     * Call Method for creating user info
     *
     * @param {*} val
     * @param {*} oldVal
     */
    @Watch('user', { immediate: true, deep: true })
    onUserChanged(val: any, oldVal: any): any {
        if (val && !isEqual(val, oldVal)) {
            this.createUserInfo();
            this.createUserModel();
        }
    }

    /**
     * Create User Info Array
     *
     * @param {object} info
     */
    createUserInfo(user: { [k: string]: any } = this.user) {
        this.userInfo = [];
        const exclude = ['name_first', 'name_last', 'mobile', 'tel', 'gender', 'address'];

        // Create Full name
        if (!this.excludeInfo.includes('name_full')) {
            let fullName = '';
            if (user.name_first) {
                fullName += user.name_first;
            }
            if (user.name_last) {
                fullName += ` ${user.name_last}`;
            }
            this.userInfo.push({
                slug : 'name_full',
                value: fullName,
                order: this.userMetadata.fields[0].fields.find((f) => f.slug === 'name_first')?.order || 1,
            });
        }

        // Create Mobile
        if (!this.excludeInfo.includes('mobile') && user.mobile.mobile) {
            let mobile = '';
            if (user.mobile.country_code) {
                mobile = `(${user.mobile.country_code}) `;
            }
            mobile += user.mobile.mobile;

            this.userInfo.push({
                slug : 'mobile',
                value: mobile,
                order: this.userMetadata.fields[0].fields.find((f) => f.slug === 'mobile')?.order || 1,
            });
        }

        // Tel Value
        if (!this.excludeInfo.includes('tel') && user.tel && user.tel.length) {
            const tel = user.tel[0].phone;
            this.userInfo.push({
                slug : 'tel',
                value: tel,
                order: this.userMetadata.fields[0].fields.find((f) => f.slug === 'tel')?.order || 1,
            });
        }

        // Gender
        if (!this.excludeInfo.includes('gender')) {
            let value = 'not_selected';
            if (user.gender === 1) {
                value = 'male';
            } else if (user.gender === 2) {
                value = 'female';
            }
            this.userInfo.push({
                slug : 'gender',
                value: this.$t(`dashboard.gender.${value}`, this.$i18n.locale),
                order: this.userMetadata.fields[0].fields.find((f) => f.slug === 'gender')?.order || 1,
            });
        }

        Object.keys(user).forEach((key) => {
            if (!this.excludeInfo.includes(key) && !exclude.includes(key)) {
                this.userInfo.push({
                    slug : key,
                    value: user[key],
                    order: this.userMetadata.fields[0].fields.find((f) => f.slug === key)?.order || 1,
                });
            }
        });
        this.userInfo.sort((a, b) => a.order - b.order);
    }

    /**
     * Create User Model
     *
     * @param {object} user
     */
    createUserModel(user: object = this.user) {
        const exclude = ['tel', 'mobile', 'address'];
        Object.keys(user).forEach((key) => {
            if (!exclude.includes(key)) {
                this.model[key] = user[key];
            }
            if (key === 'tel') {
                if (user[key] && user[key].length) {
                    this.model[key] = user[key][0].phone;
                }
            }
            if (key === 'avatar') {
                this.model[key] = user[key]['0'];
            }

            if (key === 'mobile') {
                if (user[key]) {
                    this.model[key] = user[key].mobile;
                }
            }
        });
    }

    /**
     * Save Profile
     *
     * @returns {*}
     */
    saveEdit(): any {
        this.$wait.start('save-profile-edit');

        // make tel array
        const { tel } = this.model;
        const oldTel = this.user.tel && this.user.tel.length ? this.user.tel[0] : {};
        delete this.model.tel;
        if (tel) {
            this.model.tel = [
                {
                    country_code: oldTel.country_code,
                    id          : oldTel.id,
                    phone       : tel,
                },
            ];
        }

        const params = {
            ...this.user,
            ...this.model,
        };
        return this.$services[this.editService.name][this.editService.api](params)
            .then(() => {
                this.$toast.success(this.successEditMessage || this.$t('messages.success.save_edit'));
                this.$emit('save');
                this.$router.push({ name: 'dashboard-profile' });
            })
            .catch((error: object) => {
                this.handleError(error);
            })
            .finally(() => {
                this.$wait.end('save-profile-edit');
            });
    }

    /**
     * Save Profile
     *
     * @param {*} value
     */
    changeAvatar(value) {
        this.model.avatar = value;
    }
}
